import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Image } from 'expo-image';
import { XIcon } from 'lucide-react-native';
import React, { useState } from 'react';
import { Dimensions, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { attachments_dicom_do, attachments_dicom_dont } from '@/assets/images';
import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetScrollView,
} from '@/components/ui/actionsheet';
import { Box } from '@/components/ui/box';
import { Button, ButtonText } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Modal, ModalBackdrop, ModalBody, ModalContent } from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { SUPPORTED_FILE_TYPES_DISPLAY } from '@/constants/fileTypes';
import { useHaptic } from '@/hooks/useHaptic';
import { segmentClient } from '@/util/analytics';

interface InquiryGuidelinesProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'message_guidelines' | 'attachment_guidelines';
  context?: 'inquiry_form' | 'demo_modal' | 'surgeon_attachment_reminder';
}

/**
 * InquiryGuidelines Component
 *
 * Displays guidelines for creating medical inquiries with two tabs:
 * - Message Guidelines: Instructions for writing inquiry messages
 * - Attachment Guidelines: Instructions for uploading files safely
 *
 * @param isOpen - Controls modal/actionsheet visibility
 * @param onClose - Callback when user closes the guidelines
 * @param initialTab - Which tab to show initially (defaults to 'message_guidelines')
 *
 * @behavior
 * - Uses Modal on web, Actionsheet on native
 * - Resets to initialTab whenever opened to ensure correct tab is shown
 * - Only shows attachment tab when explicitly requested (e.g., from surgeon attachments)
 */

function MessageGuidelinesTab() {
  return (
    <VStack>
      <Box className="pb-3">
        <Text className="font-normal text-sm leading-[21px] text-typography-950">
          Remove all patient identifiable information (PHI) before submitting your inquiry or
          uploading attachments.
        </Text>
      </Box>

      <Box>
        <Heading size="md" className="text-left text-typography-950">
          Patient Case Questions:
        </Heading>
        <Text className="mt-2 font-normal text-sm leading-[21px] text-typography-950">
          Include the patient's treatment history and your specific question. Add attachments if
          helpful (no PHI). The more detail you provide, the better the expert's response.
        </Text>
      </Box>

      <Box className="mt-4">
        <Heading size="md" className="text-left text-typography-950">
          General Questions:
        </Heading>
        <Text className="mt-2 font-normal text-sm leading-[21px] text-typography-950">
          You can also ask experts about real-world, mentorship-style topics that aren't available
          on Google—for example, their experience with treatments, how they incorporate guidelines
          into practice, clinical trial availability, and perspectives on recent data.
        </Text>
      </Box>

      <Box className="mt-4">
        <Heading size="md" className="text-left text-typography-950">
          Example Inquiry:
        </Heading>
        <Text className="mt-2 font-normal text-sm italic leading-[21px] text-typography-950">
          "For patients with good performance status and minimal medical comorbidities who have
          pentarefractory myeloma with progressive disease, when would you consider GPRC5D
          bispecific as a bridge to BCMA CAR-T? Who is a good patient that would not require
          bridging therapy? There are many different strategies at this stage, but I am trying to
          develop a better understanding of how these therapies can be incorporated into practice."
        </Text>
      </Box>
    </VStack>
  );
}

function AttachmentGuidelinesTab() {
  return (
    <VStack>
      <Box className="pb-3">
        <Text className="font-normal text-sm leading-[21px] text-typography-950">
          Remove all patient identifiable information (PHI) before submitting your inquiry or
          uploading attachments.
        </Text>
      </Box>

      <Box>
        <Heading size="md" className="text-left text-typography-950">
          Preparing Attachments:
        </Heading>
        <VStack className="mt-2 space-y-1">
          <Text className="ml-2 font-normal text-sm leading-[21px] text-typography-950">
            <Text className="font-thin">•</Text> {SUPPORTED_FILE_TYPES_DISPLAY}
          </Text>

          <Text className="ml-2 font-normal text-sm leading-[21px] text-typography-950">
            <Text className="font-thin">•</Text> Redact all identifiers
          </Text>

          <Text className="ml-2 font-normal text-sm leading-[21px] text-typography-950">
            <Text className="font-thin">•</Text> Rename files (no patient names)
          </Text>

          <Text className="ml-2 font-normal text-sm leading-[21px] text-typography-950">
            <Text className="font-thin">•</Text> Check embedded metadata (e.g., DICOM headers)
          </Text>
        </VStack>
      </Box>

      <Box className="mt-4">
        <Heading size="md" className="text-left text-typography-950">
          Imaging & DICOM Files:
        </Heading>
        <Text className="mt-2 font-normal text-sm leading-[21px] text-typography-950">
          For DICOMs, the simplest method is to take a screenshot, short screen recording or phone
          video that clearly shows the relevant anatomy or finding while cropping out any PHI (name
          banners or corner labels).
        </Text>
      </Box>

      <Box className="mt-4">
        <Box className="flex w-full flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <Box className="w-full sm:flex-1">
            <Text className="mb-3 block text-left font-semibold text-sm text-typography-950">
              ✅ Do
            </Text>
            <Image
              alt="Do DICOM Image"
              source={attachments_dicom_do}
              contentFit="contain"
              contentPosition="left"
              style={{ width: '100%', aspectRatio: 16 / 9 }}
            />
          </Box>
          <Box className="w-full sm:flex-1">
            <Text className="mb-3 block text-left font-semibold text-sm text-typography-950">
              ❌ Don't
            </Text>
            <Image
              alt="Don't DICOM Image"
              source={attachments_dicom_dont}
              contentFit="contain"
              contentPosition="left"
              style={{ width: '100%', aspectRatio: 16 / 9 }}
            />
          </Box>
        </Box>
        <Text className="mt-4 font-normal text-xs italic text-typography-950">
          Here is an example of a simple video taken on a mobile device that does not show PHI and
          communicated the clinical context.
        </Text>
      </Box>
    </VStack>
  );
}

const tabs = [
  { key: 'message_guidelines', title: 'Message Guidelines' },
  { key: 'attachment_guidelines', title: 'Attachments' },
] as const;

const CustomTabBar = ({
  activeTab,
  onTabChange,
  context = 'inquiry_form',
}: {
  activeTab: 'message_guidelines' | 'attachment_guidelines';
  onTabChange: (tab: 'message_guidelines' | 'attachment_guidelines') => void;
  context?: 'inquiry_form' | 'demo_modal' | 'surgeon_attachment_reminder';
}) => {
  const haptic = useHaptic();

  return (
    <Box className="relative w-full">
      <HStack className="relative w-full items-end">
        {tabs.map((tab, index) => (
          <Pressable
            key={tab.key}
            onPress={() => {
              haptic.selection();

              segmentClient.track(ANALYTICS_EVENTS.INQUIRY_GUIDELINES_TAB_SWITCHED, {
                from_tab: activeTab,
                to_tab: tab.key,
                context,
              });

              onTabChange(tab.key);
            }}
            className="items-start pb-2 pr-8">
            <Text
              className={twJoin(
                'font-medium text-sm',
                activeTab === tab.key ? 'text-typography-950' : 'text-typography-300'
              )}>
              {tab.title}
            </Text>
            {activeTab === tab.key && (
              <Box
                className="absolute bottom-0 left-0 right-8 h-0.5 bg-primary-500"
                style={{ zIndex: 1 }}
              />
            )}
          </Pressable>
        ))}
      </HStack>
      <Divider className="absolute bottom-0 left-0 right-0" />
    </Box>
  );
};

export function GuidelinesContent({
  activeTab,
}: {
  activeTab: 'message_guidelines' | 'attachment_guidelines';
}) {
  return activeTab === 'message_guidelines' ? (
    <MessageGuidelinesTab />
  ) : (
    <AttachmentGuidelinesTab />
  );
}

function GuidelinesFooter({ onClose }: { onClose?: () => void }) {
  if (!onClose) {
    return null;
  }

  return (
    <HStack className="mt-6 justify-end">
      <Button size="md" onPress={onClose}>
        <ButtonText>Got it</ButtonText>
      </Button>
    </HStack>
  );
}

export function InquiryGuidelinesSection({
  initialTab,
  onClose,
  context = 'inquiry_form',
}: {
  initialTab?: 'message_guidelines' | 'attachment_guidelines';
  onClose?: () => void;
  context?: 'inquiry_form' | 'demo_modal' | 'surgeon_attachment_reminder';
}) {
  const [activeTab, setActiveTab] = useState<'message_guidelines' | 'attachment_guidelines'>(
    initialTab || 'message_guidelines'
  );

  return (
    <VStack>
      <Text className="mb-4 text-left font-semibold text-lg text-typography-950">
        💡 Inquiry Guidelines
      </Text>
      <VStack>
        <CustomTabBar activeTab={activeTab} onTabChange={setActiveTab} context={context} />
        <Box className="pt-3">
          <GuidelinesContent activeTab={activeTab} />
          <GuidelinesFooter onClose={onClose} />
        </Box>
      </VStack>
    </VStack>
  );
}

export function InquiryGuidelines({
  isOpen,
  onClose,
  initialTab,
  context = 'inquiry_form',
}: InquiryGuidelinesProps) {
  // Always use Modal on web (desktop and mobile web). Use Actionsheet only on native.
  const useModal = isWeb;

  useSafeAreaInsets();

  const [activeTab, setActiveTab] = useState<'message_guidelines' | 'attachment_guidelines'>(
    initialTab || 'message_guidelines'
  );

  React.useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab || 'message_guidelines');
    }
  }, [isOpen, initialTab]);

  if (useModal) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalBackdrop />
        <ModalContent className="max-h-[80vh] max-w-[700px] p-0">
          <VStack className="w-full">
            {/* Header with padding */}
            <HStack className="w-full items-center justify-between px-6 py-6">
              <Text className="font-semibold text-lg text-typography-950">
                💡 Inquiry Guidelines
              </Text>
              <Pressable onPress={onClose} className="p-2">
                <Icon as={XIcon} className="h-5 w-5 text-background-500" />
              </Pressable>
            </HStack>

            {/* Tabs with padding */}
            <Box className="px-6">
              <CustomTabBar activeTab={activeTab} onTabChange={setActiveTab} context={context} />
            </Box>
          </VStack>

          {/* Scrollable content with padding */}
          <ModalBody className="max-h-[60vh] overflow-y-auto px-6 pb-6 pt-3">
            <GuidelinesContent activeTab={activeTab} />
            <GuidelinesFooter onClose={onClose} />
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  // Constrain native actionsheet height to show backdrop (92% of screen)
  const sheetMaxHeight = Math.round(Dimensions.get('window').height * 0.92);

  return (
    <Actionsheet
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}>
      <ActionsheetBackdrop />
      <ActionsheetContent
        className="w-full rounded-t-2xl p-0"
        style={{ maxHeight: sheetMaxHeight }}>
        <ActionsheetDragIndicatorWrapper>
          <ActionsheetDragIndicator />
        </ActionsheetDragIndicatorWrapper>

        <VStack className="z-20 w-full px-6 py-6">
          <HStack className="w-full items-center justify-between">
            <Text className="font-semibold text-lg text-typography-950">💡 Inquiry Guidelines</Text>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Close"
              className="-mr-2 shrink-0 p-2">
              <Icon as={XIcon} className="h-7 w-7 text-background-500" />
            </Pressable>
          </HStack>
          <Box className="mt-4 w-full">
            <CustomTabBar activeTab={activeTab} onTabChange={setActiveTab} context={context} />
          </Box>
        </VStack>

        <ActionsheetScrollView contentContainerClassName="px-6 pb-10">
          <GuidelinesContent activeTab={activeTab} />
          <GuidelinesFooter onClose={onClose} />
        </ActionsheetScrollView>
      </ActionsheetContent>
    </Actionsheet>
  );
}
